import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Sun, Mail, MessageCircle, Check, Loader2, ChevronLeft,
  Clock, Shield, Phone, FileText, Sparkles,
} from "lucide-react";
import { apiClient } from "../lib/api";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { trackLead as fbTrackLead } from "../lib/fbpixel";
import { trackLead as gaTrackLead, trackPageView } from "../lib/gtag";

/* QuoteRequest — Confirmation + contact-capture page for the "Get a free
   Dr. Sol quote" CTA from /v4. Reads the visitor's intake state from
   localStorage (key: `doctorsol_quote_request`) so they don't have to retype.
   Submits to /api/leads with funnel="v4-quote-request" so the back-office can
   build a personalised quote and follow up within 24h. */

const STR = {
  nl: {
    nav_back: "Terug naar berekening",
    badge: "Gratis offerte · Vrijblijvend",
    h1_l1: "Vraag je persoonlijke",
    h1_l2: "Dr. Sol offerte aan.",
    intro: "Op basis van de info die je hebt gedeeld maken we een offerte op maat. Binnen 24 uur ontvang je deze. Daarna nemen we persoonlijk contact op om alles rustig toe te lichten.",
    promise_1: "Binnen 24u in je inbox",
    promise_2: "100% gratis & vrijblijvend",
    promise_3: "Geen verkoopdruk",
    summary_kicker: "Op basis van jouw input",
    sum_bill: "Maandlasten",
    sum_city: "Locatie",
    sum_save: "Geschatte besparing (10 jaar)",
    form_h: "Waar mogen we je offerte sturen?",
    name_l: "Naam", name_ph: "Jan de Vries",
    email_l: "E-mail", email_ph: "jan@email.es",
    wa_l: "WhatsApp", wa_ph: "+34 ...",
    msg_l: "Bericht (optioneel)",
    msg_ph: "Speciale wensen, vragen of aanvullingen…",
    submit: "Verstuur mijn aanvraag",
    submit_busy: "Verzenden…",
    err_required: "Vul je naam, e-mail en WhatsApp in",
    err_email: "Vul een geldig e-mailadres in",
    confirm_h1: "Bedankt! We zijn aan het werk.",
    confirm_p: "Je persoonlijke Dr. Sol offerte staat binnen 24 uur in je inbox. Daarna nemen we persoonlijk contact op om alles toe te lichten.",
    confirm_what_next: "Wat krijg je?",
    confirm_n1: "Volledige offerte op maat (panelen, omvormer, batterij)",
    confirm_n2: "Alle subsidies en fiscale voordelen voor jouw situatie",
    confirm_n3: "Persoonlijk telefoongesprek om alles toe te lichten",
    confirm_back: "← Terug naar de berekening",
  },
  es: {
    nav_back: "Volver al cálculo",
    badge: "Presupuesto gratis · Sin compromiso",
    h1_l1: "Solicita tu presupuesto",
    h1_l2: "personal Dr. Sol.",
    intro: "En base a la información que has compartido elaboramos un presupuesto a medida. Lo recibirás en 24 horas. Después te llamamos personalmente para explicarlo todo con calma.",
    promise_1: "En tu bandeja en 24h",
    promise_2: "100% gratis y sin compromiso",
    promise_3: "Sin presión de venta",
    summary_kicker: "Basado en tu información",
    sum_bill: "Factura mensual",
    sum_city: "Ubicación",
    sum_save: "Ahorro estimado (10 años)",
    form_h: "¿Dónde te enviamos el presupuesto?",
    name_l: "Nombre", name_ph: "Juan García",
    email_l: "Email", email_ph: "juan@email.es",
    wa_l: "WhatsApp", wa_ph: "+34 ...",
    msg_l: "Mensaje (opcional)",
    msg_ph: "Peticiones especiales, dudas o detalles…",
    submit: "Enviar mi solicitud",
    submit_busy: "Enviando…",
    err_required: "Completa nombre, email y WhatsApp",
    err_email: "Introduce un email válido",
    confirm_h1: "¡Gracias! Estamos trabajando.",
    confirm_p: "Tu presupuesto Dr. Sol personalizado estará en tu bandeja en 24 horas. Después te llamamos para explicarlo todo.",
    confirm_what_next: "¿Qué recibes?",
    confirm_n1: "Presupuesto completo a medida (paneles, inversor, batería)",
    confirm_n2: "Todas las subvenciones y ventajas fiscales para tu caso",
    confirm_n3: "Llamada personal para explicarlo todo",
    confirm_back: "← Volver al cálculo",
  },
  en: {
    nav_back: "Back to calculator",
    badge: "Free quote · No obligation",
    h1_l1: "Request your personal",
    h1_l2: "Dr. Sol quote.",
    intro: "Based on the information you've shared we build a personal quote. You'll receive it within 24 hours. Then we'll call you personally to walk through everything.",
    promise_1: "In your inbox in 24h",
    promise_2: "100% free & no obligation",
    promise_3: "No sales pressure",
    summary_kicker: "Based on your input",
    sum_bill: "Monthly bill",
    sum_city: "Location",
    sum_save: "Estimated savings (10 yrs)",
    form_h: "Where shall we send the quote?",
    name_l: "Name", name_ph: "John Smith",
    email_l: "Email", email_ph: "john@email.es",
    wa_l: "WhatsApp", wa_ph: "+34 ...",
    msg_l: "Message (optional)",
    msg_ph: "Special requests, questions or details…",
    submit: "Send my request",
    submit_busy: "Sending…",
    err_required: "Please fill name, email and WhatsApp",
    err_email: "Please enter a valid email",
    confirm_h1: "Thanks! We're on it.",
    confirm_p: "Your personal Dr. Sol quote will be in your inbox within 24 hours. Then we'll call you personally to explain it all.",
    confirm_what_next: "What do you receive?",
    confirm_n1: "Full personalised quote (panels, inverter, battery)",
    confirm_n2: "Every subsidy and tax break for your situation",
    confirm_n3: "Personal call to walk you through it",
    confirm_back: "← Back to the calculator",
  },
};
const t = (lang) => STR[(lang || "es").split("-")[0].toLowerCase()] || STR.es;

export default function QuoteRequest() {
  const { i18n } = useTranslation();
  const T = t(i18n.language);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent]             = useState(false);
  const [intake, setIntake]         = useState(null);
  const [form, setForm]             = useState({ name: "", email: "", whatsapp: "", message: "" });

  useEffect(() => {
    trackPageView("/offerte");
    try {
      const raw = localStorage.getItem("doctorsol_quote_request");
      if (raw) setIntake(JSON.parse(raw));
    } catch (e) { /* ignore */ }
  }, []);

  const submit = async () => {
    if (!form.name.trim() || !form.email.trim() || !form.whatsapp.trim()) {
      toast.error(T.err_required); return;
    }
    if (!form.email.includes("@")) { toast.error(T.err_email); return; }
    setSubmitting(true);
    try {
      const lang = (i18n.language || "es").split("-")[0].toLowerCase();
      await apiClient.post("/leads", {
        name: form.name,
        email: form.email,
        whatsapp: form.whatsapp,
        region: intake?.city || "madrid",
        language: lang,
        funnel: "v4-quote-request",
        intake_preview: {
          ...(intake || {}),
          message: form.message || null,
        },
      });
      // Tracking: quote-request lead = warmest segment
      const value = Number(intake?.savings?.ten_year_save || 0);
      const leadPayload = { content_name: "DoctorSol Quote Request", value, currency: "EUR", funnel: "v4-quote-request" };
      fbTrackLead(leadPayload);
      gaTrackLead(leadPayload);
      setSent(true);
      try { localStorage.removeItem("doctorsol_quote_request"); } catch (e) { /* ignore */ }
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-[#F8F6F0] text-[#0F172A]"
         style={{ fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif", WebkitFontSmoothing: "antialiased", letterSpacing: "-0.01em" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@600;700;800&family=Inter:wght@400;500;600&display=swap');
        .h-display { font-family: 'Outfit', sans-serif; font-weight: 800; letter-spacing: -0.04em; line-height: 0.95; }
        .h-display-thin { font-family: 'Outfit', sans-serif; font-weight: 600; letter-spacing: -0.035em; }
      `}</style>

      <header className="px-5 md:px-12 lg:px-20 py-5 flex items-center justify-between border-b border-[#0F172A]/5">
        <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
          <div className="w-9 h-9 rounded-full bg-[#E78938] flex items-center justify-center"><Sun className="w-5 h-5 text-white" /></div>
          <span className="h-display-thin text-xl">DoctorSol</span>
        </Link>
        <div className="flex items-center gap-3">
          <LanguageSwitcher variant="minimal" />
          <Link to="/v4" data-testid="nav-back"
            className="hidden md:inline-flex items-center gap-1 text-sm text-[#475569] hover:text-[#0F172A]">
            <ChevronLeft className="w-4 h-4" /> {T.nav_back}
          </Link>
        </div>
      </header>

      <section className="px-5 md:px-12 lg:px-20 py-12 md:py-20">
        <div className="max-w-3xl mx-auto">
          {!sent ? (
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }}>
              <div className="inline-flex items-center gap-2 bg-[#E78938]/10 text-[#E78938] px-3.5 py-1.5 rounded-full text-[12px] font-semibold border border-[#E78938]/20 mb-7">
                <Sparkles className="w-3.5 h-3.5" />
                {T.badge}
              </div>
              <h1 className="h-display text-[40px] sm:text-[52px] lg:text-[64px] mb-6">
                {T.h1_l1}<br/>
                <span className="text-[#E78938]">{T.h1_l2}</span>
              </h1>
              <p className="text-lg text-[#475569] leading-relaxed max-w-xl mb-8">{T.intro}</p>

              {/* Promises strip */}
              <div className="flex flex-wrap gap-x-6 gap-y-3 mb-10 text-sm text-[#0F172A]">
                <span className="flex items-center gap-2"><Clock className="w-4 h-4 text-[#16A34A]" /> {T.promise_1}</span>
                <span className="flex items-center gap-2"><Shield className="w-4 h-4 text-[#16A34A]" /> {T.promise_2}</span>
                <span className="flex items-center gap-2"><Check className="w-4 h-4 text-[#16A34A]" /> {T.promise_3}</span>
              </div>

              {/* Intake summary (only if data exists) */}
              {intake && (
                <div className="bg-white border border-[#0F172A]/5 rounded-2xl p-5 mb-8 shadow-sm">
                  <div className="text-xs uppercase tracking-wider text-[#E78938] font-bold mb-3">{T.summary_kicker}</div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
                    {intake.monthly_eur && (
                      <SumCell label={T.sum_bill} value={`€${intake.monthly_eur}/mo`} />
                    )}
                    {intake.city && (
                      <SumCell label={T.sum_city} value={intake.city} />
                    )}
                    {intake.savings?.ten_year_save && (
                      <SumCell label={T.sum_save}
                        value={`€${Number(intake.savings.ten_year_save).toLocaleString("es-ES")}`}
                        accent />
                    )}
                  </div>
                </div>
              )}

              {/* Contact form */}
              <div className="bg-white border border-[#0F172A]/5 rounded-3xl p-6 md:p-8 shadow-sm">
                <h2 className="h-display-thin text-xl mb-5">{T.form_h}</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label={T.name_l} placeholder={T.name_ph}
                    value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="qr-name" required />
                  <Field label={T.email_l} placeholder={T.email_ph} type="email"
                    value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="qr-email" required />
                  <Field label={T.wa_l} placeholder={T.wa_ph} type="tel"
                    value={form.whatsapp} onChange={(v) => setForm({ ...form, whatsapp: v })} testid="qr-wa" required />
                </div>
                <div className="mt-4">
                  <label className="text-base font-semibold text-[#0F172A] block mb-2">{T.msg_l}</label>
                  <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                    placeholder={T.msg_ph} rows={3} data-testid="qr-msg"
                    className="w-full bg-[#F8F6F0] border-2 border-transparent rounded-2xl px-4 py-3 text-base resize-none focus:outline-none focus:border-[#E78938]" />
                </div>
                <button onClick={submit} disabled={submitting} data-testid="qr-submit"
                  className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#E78938] hover:bg-[#D17627] text-white rounded-full px-6 py-4 font-semibold text-base transition-colors disabled:opacity-60">
                  {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <FileText className="w-5 h-5" />}
                  {submitting ? T.submit_busy : T.submit}
                </button>
              </div>
            </motion.div>
          ) : (
            /* ───── Confirmation ──────────────────────────────────────── */
            <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }} className="text-center">
              <div className="w-20 h-20 bg-[#16A34A] rounded-full flex items-center justify-center mx-auto mb-7 shadow-lg shadow-[#16A34A]/30">
                <Check className="w-10 h-10 text-white" strokeWidth={3} />
              </div>
              <h1 className="h-display text-4xl md:text-5xl mb-5">{T.confirm_h1}</h1>
              <p className="text-lg text-[#475569] leading-relaxed mb-10 max-w-xl mx-auto">{T.confirm_p}</p>

              <div className="bg-white border border-[#0F172A]/5 rounded-2xl p-6 md:p-8 text-left max-w-xl mx-auto mb-10">
                <h3 className="h-display-thin text-lg mb-4">{T.confirm_what_next}</h3>
                <ul className="space-y-3 text-[#0F172A]">
                  <li className="flex items-start gap-3"><FileText className="w-5 h-5 text-[#E78938] mt-0.5 shrink-0" /> <span>{T.confirm_n1}</span></li>
                  <li className="flex items-start gap-3"><Sparkles className="w-5 h-5 text-[#E78938] mt-0.5 shrink-0" /> <span>{T.confirm_n2}</span></li>
                  <li className="flex items-start gap-3"><Phone className="w-5 h-5 text-[#E78938] mt-0.5 shrink-0" /> <span>{T.confirm_n3}</span></li>
                </ul>
              </div>

              <Link to="/" data-testid="qr-back-home"
                className="inline-flex items-center gap-2 text-sm text-[#475569] hover:text-[#E78938] transition-colors">
                {T.confirm_back}
              </Link>
            </motion.div>
          )}
        </div>
      </section>

      <footer className="border-t border-[#0F172A]/10 px-5 md:px-12 lg:px-20 py-8 text-xs text-[#94A3B8] flex flex-wrap justify-between gap-3">
        <div>© 2026 DoctorSol</div>
        <div className="flex gap-5"><a href="/privacy">Privacy</a><a href="/voorwaarden">Voorwaarden</a><a href="mailto:info@doctorsol.es">Contact</a></div>
      </footer>
    </div>
  );
}

const Field = ({ label, placeholder, value, onChange, type = "text", testid, required }) => (
  <div>
    <label className="text-base font-semibold text-[#0F172A] block mb-2">
      {label}{required && <span className="text-[#E78938]"> *</span>}
    </label>
    <input type={type} value={value} placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)} data-testid={testid}
      className="w-full bg-[#F8F6F0] border-2 border-transparent rounded-2xl px-4 py-3 text-base focus:outline-none focus:border-[#E78938]" />
  </div>
);

const SumCell = ({ label, value, accent }) => (
  <div>
    <div className="text-xs uppercase tracking-wider text-[#94A3B8] font-bold mb-1">{label}</div>
    <div className={`h-display-thin text-base ${accent ? "text-[#16A34A] text-lg" : "text-[#0F172A]"}`}>{value}</div>
  </div>
);
